// ---------------------------------------------------------------------------
// PWA_ControlBillet — Scan result card
// Shown after scanning. Three variants: valid, invalid/expired, duplicate.
// Auto-dismiss after 5s. Tap to dismiss.
// ---------------------------------------------------------------------------

import { useEffect, useRef } from 'react';
import type { ScanResult as ScanResultType } from '@/lib/types';

interface Props {
  result: ScanResultType | null;
  onDismiss: () => void;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function CheckIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-white"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-white"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-white"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimeSince(ms: number | undefined): string {
  if (ms == null) return '';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `il y a ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `il y a ${hours}h`;
}

// ---------------------------------------------------------------------------
// Variant config
// ---------------------------------------------------------------------------

interface VariantConfig {
  bg: string;
  border: string;
  title: string;
  icon: React.ReactNode;
  animation: string;
}

function getVariant(type: ScanResultType['type']): VariantConfig {
  switch (type) {
    case 'valid':
    case 'offline':
      return {
        bg: 'bg-success/95',
        border: 'border-green-400',
        title: 'Validation reussie',
        icon: <CheckIcon />,
        animation: 'animate-pulse-success',
      };
    case 'invalid':
    case 'expired':
      return {
        bg: 'bg-danger/95',
        border: 'border-red-400',
        title: 'Validation echouee',
        icon: <XIcon />,
        animation: 'animate-shake',
      };
    case 'duplicate':
      return {
        bg: 'bg-duplicate/95',
        border: 'border-yellow-400',
        title: 'Ticket deja scanne',
        icon: <WarningIcon />,
        animation: 'animate-shake',
      };
    default:
      return {
        bg: 'bg-surface-card/95',
        border: 'border-gray-500',
        title: 'Resultat inconnu',
        icon: <XIcon />,
        animation: '',
      };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScanResult({ result, onDismiss }: Props) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!result) return;

    timerRef.current = setTimeout(() => {
      onDismiss();
    }, 5000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [result, onDismiss]);

  if (!result) return null;

  const variant = getVariant(result.type);

  return (
    <div
      className="absolute inset-x-4 bottom-24 z-30 animate-fade-in"
      role="status"
      onClick={onDismiss}
    >
      <div
        className={`${variant.bg} ${variant.animation} rounded-2xl p-5 border ${variant.border} shadow-2xl backdrop-blur-sm`}
      >
        {/* Header: icon + title */}
        <div className="flex items-center gap-3 mb-3">
          {variant.icon}
          <div>
            <h3 className="text-white text-lg font-bold">{variant.title}</h3>
            {result.message && (
              <p className="text-white/80 text-sm">{result.message}</p>
            )}
          </div>
        </div>

        {/* Ticket details (valid) */}
        {(result.type === 'valid' || result.type === 'offline') &&
          result.ticketData && (
            <div className="space-y-1 ml-[60px]">
              <p className="text-white/90 text-sm">
                <span className="text-white/60">Ticket:</span>{' '}
                {result.ticketData.ticket_id}
              </p>
              <p className="text-white/90 text-sm">
                <span className="text-white/60">Client:</span>{' '}
                {result.ticketData.customer_name}
              </p>
              {result.ticketData.ticket_type && (
                <p className="text-white/90 text-sm">
                  <span className="text-white/60">Type:</span>{' '}
                  {result.ticketData.ticket_type}
                </p>
              )}
            </div>
          )}

        {/* Error details (invalid / expired) */}
        {(result.type === 'invalid' || result.type === 'expired') &&
          result.error && (
            <p className="text-white/80 text-xs ml-[60px] mt-1">
              Code: {result.error}
            </p>
          )}

        {/* Duplicate info */}
        {result.type === 'duplicate' && result.previousScan && (
          <p className="text-white/80 text-sm ml-[60px] mt-1">
            Dernier scan:{' '}
            {formatTimeSince(
              Date.now() - new Date(result.previousScan.timestamp).getTime(),
            )}
          </p>
        )}

        {/* Offline badge */}
        {result.offline && (
          <div className="mt-3 ml-[60px]">
            <span className="bg-warning/30 text-warning text-xs font-semibold px-2 py-0.5 rounded-full">
              Hors ligne
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
